/**
 * CommentComposer molecule — InputGroup comment field + "Add comment" action.
 * Size is set once on InputGroup so Input + Button share a matched scale.
 * All copy via i18next talentPool namespace.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from '@/components/ui/molecules/InputGroup';

export interface CommentComposerProps {
  onSubmit: (body: string) => void;
  isPending?: boolean;
}

export const CommentComposer: React.FC<CommentComposerProps> = ({
  onSubmit,
  isPending = false,
}) => {
  const { t } = useTranslation('talentPool');
  const [body, setBody] = useState('');

  const handleSubmit = () => {
    const trimmed = body.trim();
    if (!trimmed || isPending) return;
    onSubmit(trimmed);
    setBody('');
  };

  const isEmpty = body.trim().length === 0;

  return (
    <InputGroup size="md">
      <InputGroupInput
        type="text"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            handleSubmit();
          }
        }}
        placeholder={t('notes.addPlaceholder')}
        rounded="full"
        disabled={isPending}
        endAddonPadding="none"
        className="!pr-44"
        aria-label={t('notes.addPlaceholder')}
      />
      <InputGroupAddon align="inline-end">
        <InputGroupButton
          type="contained"
          intent="secondary"
          label={t('notes.addButton')}
          disabled={isEmpty || isPending}
          loading={isPending}
          onClick={handleSubmit}
        />
      </InputGroupAddon>
    </InputGroup>
  );
};

CommentComposer.displayName = 'CommentComposer';
